import { BufferGeometry, Float32BufferAttribute, Vector3 } from "three";
import earcut from "earcut";
import { Cartographic } from "../../math";
/**
 * Compute the unique hash of a vertex
 *
 * @param vertex
 */
function hashVertex(vertex) {
    return `${vertex.x}|${vertex.y}|${vertex.z}`;
}
/**
 * Convert a ThreeJS BufferGeometry into a triangle set
 *
 * @param geometry BufferGeometry object to convert
 * @returns
 */
function geometryToMesh(geometry) {
    const indices = geometry.getIndex();
    if (indices === null) {
        throw new Error("Geometry object does not have a list of indices.");
    }
    const positions = geometry.getAttribute("position");
    // Create each triangle
    const mesh = [];
    for (let offset = 0; offset < indices.count; offset += 3) {
        const triangle = [];
        for (let v = 0; v < 3; v++) {
            const i = indices.array[offset + v];
            const x = positions.array[i * 3 + 0];
            const y = positions.array[i * 3 + 1];
            const z = positions.array[i * 3 + 2];
            triangle.push(new Vector3(x, y, z));
        }
        mesh.push(triangle);
    }
    return mesh;
}
/**
 * Find the indices of the vertices on the edge of the quadrant
 *
 * @param positions Position buffer
 * @param translation World translation of the tile
 * @param quadrant Relevant region of the globe to take edges from
 * @param tilingScheme Tiling scheme of the terrain provider
 */
function findEdgeIndices(positions, translation, quadrant, tilingScheme) {
    const vertexCount = positions.length / 3;
    const epsilon = 1e-7;
    // Find all vertices on the edges of the quadrant
    const westEdgeIndices = [];
    const southEdgeIndices = [];
    const eastEdgeIndices = [];
    const northEdgeIndices = [];
    const scratchCartographic = new Cartographic();
    const scratchPos = new Vector3();
    for (let i = 0; i < vertexCount; i++) {
        const x = positions[i * 3 + 0];
        const y = positions[i * 3 + 1];
        const z = positions[i * 3 + 2];
        scratchPos.set(x, y, z).add(translation);
        tilingScheme.ellipsoid.cartographicForPosition(scratchPos, scratchCartographic);
        // Test if the vertex is on any of the edges
        if (Math.abs(scratchCartographic.longitude - quadrant.east) <= epsilon) {
            eastEdgeIndices.push(i);
        }
        else if (Math.abs(scratchCartographic.longitude - quadrant.west) <= epsilon) {
            westEdgeIndices.push(i);
        }
        if (Math.abs(scratchCartographic.latitude - quadrant.south) <= epsilon) {
            southEdgeIndices.push(i);
        }
        else if (Math.abs(scratchCartographic.latitude - quadrant.north) <= epsilon) {
            northEdgeIndices.push(i);
        }
    }
    return [westEdgeIndices, southEdgeIndices, eastEdgeIndices, northEdgeIndices];
}
/**
 * Generate the skirt geometry
 *
 * @param positions Position buffer of the base mesh
 * @param uvs UV buffer of the base mesh
 * @param translation World translation of the tile
 * @param quadrant Relevant region of the globe to take edges from
 * @param tilingScheme Tiling scheme of the terrain provider
 * @returns
 */
function calculateSkirtGeometry(positions, uvs, translation, quadrant, tilingScheme) {
    const edgeIndices = findEdgeIndices(positions, translation, quadrant, tilingScheme);
    const [westEdgeIndices, southEdgeIndices, eastEdgeIndices, northEdgeIndices] = edgeIndices;
    // Sort indices for triangulation
    westEdgeIndices.sort((a, b) => uvs[a * 2 + 1] - uvs[b * 2 + 1]);
    eastEdgeIndices.sort((a, b) => uvs[b * 2 + 1] - uvs[a * 2 + 1]);
    southEdgeIndices.sort((a, b) => uvs[b * 2] - uvs[a * 2]);
    northEdgeIndices.sort((a, b) => uvs[a * 2] - uvs[b * 2]);
    const skirtPositions = [];
    const skirtUVs = [];
    const skirtIndices = [];
    const scratchCartographic = new Cartographic();
    const scratchPos = new Vector3();
    for (const indices of edgeIndices) {
        const offset = skirtPositions.length / 3;
        for (const i of indices) {
            const x = positions[i * 3 + 0];
            const y = positions[i * 3 + 1];
            const z = positions[i * 3 + 2];
            // Extrude the edge vertices down 1000 world units
            scratchPos.set(x, y, z).add(translation);
            tilingScheme.ellipsoid.cartographicForPosition(scratchPos, scratchCartographic);
            scratchCartographic.height -= 10000;
            const extrude = tilingScheme.ellipsoid
                .cartesianForCartographic(scratchCartographic, scratchPos)
                .sub(translation);
            // Add vertex positions
            skirtPositions.push(x);
            skirtPositions.push(y);
            skirtPositions.push(z);
            skirtPositions.push(extrude.x);
            skirtPositions.push(extrude.y);
            skirtPositions.push(extrude.z);
            // Copy UV position of the original vertex
            skirtUVs.push(uvs[i * 2 + 0]);
            skirtUVs.push(uvs[i * 2 + 1]);
            skirtUVs.push(uvs[i * 2 + 0]);
            skirtUVs.push(uvs[i * 2 + 1]);
        }
        // Generate skirt indices
        for (let j = 0; j < indices.length * 2 - 3; j++) {
            const curr = offset + j;
            const next = offset + (j + 2);
            const upperL = curr;
            const lowerL = curr + 1;
            const upperR = next;
            const lowerR = next + 1;
            skirtIndices.push(lowerL);
            skirtIndices.push(upperL);
            skirtIndices.push(lowerR);
            skirtIndices.push(upperR);
            skirtIndices.push(lowerR);
            skirtIndices.push(upperL);
        }
    }
    const geometry = new BufferGeometry();
    geometry.setAttribute("position", new Float32BufferAttribute(skirtPositions, 3, false));
    geometry.setAttribute("uv", new Float32BufferAttribute(skirtUVs, 2, false));
    geometry.setIndex(skirtIndices);
    return geometry;
}
/**
 * Convert a triangle set into a ThreeJS BufferGeometry object
 *
 * @param mesh Mesh to convert
 * @param translation World position of the mesh
 * @param quadrant Quadrant region of the tile
 * @param tilingScheme Tiling scheme of the terrain provider
 * @returns
 */
function meshToGeometry(mesh, translation, quadrant, tilingScheme) {
    // Fill vertex map and compute indices
    const vertexSet = new Map();
    let vertexCount = 0;
    for (const triangle of mesh) {
        for (const vertex of triangle) {
            const hash = hashVertex(vertex);
            if (!vertexSet.has(hash)) {
                vertexSet.set(hash, [vertex, vertexCount++]);
            }
        }
    }
    // Compute min and max for calculating the new texture coordinates
    const uvMin = tilingScheme.projection.project(quadrant.southwest).setZ(0);
    const uvMax = tilingScheme.projection.project(quadrant.northeast).setZ(1);
    const uvInterval = uvMax.sub(uvMin);
    // Fill the coordinate buffers
    const positions = new Float32Array(3 * vertexCount);
    const uvs = new Float32Array(2 * vertexCount);
    for (const [vertex, i] of vertexSet.values()) {
        // Compute texel coordinate
        const cartesian = vertex.clone().add(translation);
        const cartographic = tilingScheme.ellipsoid.cartographicForPosition(cartesian);
        const uv = tilingScheme.projection.project(cartographic).sub(uvMin).divide(uvInterval);
        positions[i * 3 + 0] = vertex.x;
        positions[i * 3 + 1] = vertex.y;
        positions[i * 3 + 2] = vertex.z;
        uvs[i * 2 + 0] = uv.x;
        uvs[i * 2 + 1] = uv.y;
    }
    // Fill index buffer
    const indices = [];
    for (const triangle of mesh) {
        for (const vertex of triangle) {
            const hash = hashVertex(vertex);
            indices.push(vertexSet.get(hash)[1]);
        }
    }
    // Create the geometry
    const geometry = new BufferGeometry();
    geometry.setAttribute("position", new Float32BufferAttribute(positions, 3, false));
    geometry.setAttribute("uv", new Float32BufferAttribute(uvs, 2, false));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    geometry.computeBoundingSphere();
    // Create the skirt geometry
    const skirtGeometry = calculateSkirtGeometry(positions, uvs, translation, quadrant, tilingScheme);
    return [geometry, skirtGeometry];
}
/**
 * Calculate the quadrant of the parent's rectangular region that corresponds to the desired tile node.
 *
 * @param x X coordinate of the desired tile
 * @param y Y coordinate of the desired tile
 * @param parent Parent node of the desired tile
 * @param tilingScheme Tiling scheme of the terrain provider
 * @param region Relevant rectangular region
 */
function calculateQuadrant(x, y, parent, tilingScheme, region) {
    // Calculate the relevant rectangular region on the globe
    const rectangle = tilingScheme.tileXYToRectangle(parent.x, parent.y, parent.zoomLevel).intersection(region);
    const center = rectangle.midpoint;
    // Determines which quadrant the tile belongs to
    const xIndex = x - parent.x * 2;
    const yIndex = y - parent.y * 2;
    // Update the bounds of the rectangle
    if (xIndex === 0) {
        rectangle.east = center.longitude;
    }
    else {
        rectangle.west = center.longitude;
    }
    if (yIndex === 0) {
        rectangle.south = center.latitude;
    }
    else {
        rectangle.north = center.latitude;
    }
    return rectangle;
}
/**
 * Calculate the centroid of a triangle
 *
 * @param triangle
 */
function calculateCentroid(triangle) {
    const centroid = new Vector3();
    centroid.add(triangle[0]).add(triangle[1]).add(triangle[2]);
    return centroid.divideScalar(3);
}
/**
 * Calculate the surface normal of the triangle
 *
 * @param triangle
 * @returns
 */
function calculateNormal(triangle) {
    const a = triangle[0];
    const b = triangle[1];
    const c = triangle[2];
    const ab = b.clone().sub(a);
    const ac = c.clone().sub(a);
    return ab.cross(ac);
}
/**
 * Triangulate a polygon
 *
 * @param polygon Polygon to triangulate
 * @param tilingScheme Tiling scheme of the terrain provider
 * @returns
 */
function triangulate(polygon, tilingScheme) {
    // Skip trivial cases
    if (polygon.length === 3)
        return [polygon];
    if (polygon.length === 0)
        return [];
    // Flatten vertex list
    const flatVertices = [];
    polygon.forEach((vertex) => {
        flatVertices.push(vertex.x);
        flatVertices.push(vertex.y);
        flatVertices.push(vertex.z);
    });
    const indices = earcut(flatVertices, [], 3);
    const triangles = [];
    for (let i = 0; i < indices.length - 2; i += 3) {
        const t0 = [polygon[indices[i]], polygon[indices[i + 1]], polygon[indices[i + 2]]];
        const t1 = [polygon[indices[i + 2]], polygon[indices[i + 1]], polygon[indices[i]]];
        // Choose the winding order which makes the triangle visible from above the globe surface
        const centroid = calculateCentroid(t0);
        const surfaceNormal = tilingScheme.ellipsoid.geodeticSurfaceNormalForPosition(centroid);
        const n0 = calculateNormal(t0);
        if (surfaceNormal.dot(n0) >= 0) {
            triangles.push(t0);
        }
        else {
            triangles.push(t1);
        }
    }
    return triangles;
}
/**
 * Test if a cartographic point is within the edge formed by c0 and c1
 *
 * @param point Query point
 * @param edge0 Start point of the quadrant edge
 * @param edge1 End point of the quadrant edge
 * @returns
 */
function isInEdge(point, edge0, edge1) {
    // 2D Cross product
    const r0 = (edge1.longitude - edge0.longitude) * (point.latitude - edge0.latitude);
    const r1 = (edge1.latitude - edge0.latitude) * (point.longitude - edge0.longitude);
    return r0 - r1 <= 0;
}
/**
 * Calculate the intersection point between two edges formed by cartographic coordinates
 *
 * @param point0 Start point on triangle
 * @param point1 End point on triangle
 * @param edge0 Start point of the quadrant edge
 * @param edge1 End point of the quadrant edge
 */
function calculateIntersection(point0, point1, edge0, edge1) {
    // Treat cartographic coordinates as 'Vector3' to perform calculations
    const p0 = new Vector3(point0.longitude, point0.latitude, point0.height);
    const p1 = new Vector3(point1.longitude, point1.latitude, point1.height);
    const e0 = new Vector3(edge0.longitude, edge0.latitude, edge0.height);
    const e1 = new Vector3(edge1.longitude, edge1.latitude, edge1.height);
    // Calculate intersection plane normal (i.e., extend the height of the quadrant edge to infinity)
    const normal = new Vector3();
    const edge = e1.clone().sub(e0);
    if (edge.x === 0) {
        // Vertical axis
        normal.x = Math.sign(edge.y);
    }
    else if (edge.y === 0) {
        // Horizontal axis
        normal.y = -Math.sign(edge.x);
    }
    else {
        throw new Error("Invalid edge is not parallel to the latitudinal / longitudinal axes");
    }
    // Calculate interpolation factor between p0 and p1 to generate the intersection point
    const line = p1.clone().sub(p0);
    const t = (normal.dot(e0) - normal.dot(p0)) / normal.dot(line);
    const intersection = new Vector3().lerpVectors(p0, p1, t);
    return new Cartographic(intersection.x, intersection.y, intersection.z);
}
/**
 * Clip a triangle so it fits inside the quadrant, returning a set of child triangles
 *
 * If the triangle is outside the quadrant, discard it
 * If the triangle is fully within the quadrant, include it
 * If the triangle is on the boundary, split it
 *
 * @param triangle Triangle in world coordinates to be clipped
 * @param quadrant Quadrant region of the tile
 * @param tilingScheme Tiling scheme of the terrain provider
 */
function clipTriangle(triangle, quadrant, tilingScheme) {
    // Clip the triangle in geographic coordinate space using the Sutherland-Hodgman algorithm
    const corners = [quadrant.southwest, quadrant.northwest, quadrant.northeast, quadrant.southeast];
    let clipped = triangle.map((vertex) => tilingScheme.ellipsoid.cartographicForPosition(vertex));
    for (let i = 0; i < corners.length; i++) {
        const edge0 = corners[i];
        const edge1 = corners[(i + 1) % corners.length];
        const result = [];
        for (let j = 0; j < clipped.length; j++) {
            const prev = clipped[j];
            const curr = clipped[(j + 1) % clipped.length];
            if (isInEdge(curr, edge0, edge1)) {
                if (!isInEdge(prev, edge0, edge1)) {
                    result.push(calculateIntersection(prev, curr, edge0, edge1));
                }
                result.push(curr);
            }
            else if (isInEdge(prev, edge0, edge1)) {
                result.push(calculateIntersection(prev, curr, edge0, edge1));
            }
        }
        clipped = result;
    }
    // Convert back to cartesian and triangulate
    const cartesian = clipped.map((position) => tilingScheme.ellipsoid.cartesianForCartographic(position));
    return triangulate(cartesian, tilingScheme);
}
/**
 * Split a tile's parent and return the tile geometry for the desired quadrant
 *
 * @param tile Terrain geometry of the parent
 * @param x X coordinate of the desired tile
 * @param y Y coordinate of the desired tile
 * @param parent Parent node of the desired tile
 * @param tilingScheme Tiling scheme of the terrain provider
 * @param region Relevant rectangular region
 */
export function splitTile(tile, x, y, parent, tilingScheme, region) {
    const quadrant = calculateQuadrant(x, y, parent, tilingScheme, region);
    const parentMesh = geometryToMesh(tile.geometry);
    const mesh = [];
    for (const triangle of parentMesh) {
        // Convert triangle coordinates to world space
        const worldTriangle = triangle.map((vertex) => vertex.clone().add(tile.midpoint));
        // Clip the triangle and get the resulting children
        const children = clipTriangle(worldTriangle, quadrant, tilingScheme);
        // Convert the children back into local space before including in the mesh
        for (const child of children) {
            const subTriangle = child.map((vertex) => vertex.clone().sub(tile.midpoint));
            mesh.push(subTriangle);
        }
    }
    const [geometry, skirtGeometry] = meshToGeometry(mesh, tile.midpoint, quadrant, tilingScheme);
    return {
        geometry,
        skirtGeometry,
        midpoint: tile.midpoint,
    };
}
